import { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FormDialog } from 'components';
import { reviewPriceDialogColors as colors } from 'pages/Intelligence/DailyNav/components/ReviewPriceDialog/ReviewPriceDialog.styles';
import { useDailyNavOverrideForm, useOverrideDailyNavPrice } from 'services/hooks/firms';
import { shortDate } from 'utilities';
import {
  buildOverrideRequest,
  canEnterInReporting,
  currencyCode,
  hasCurrencyDifference,
  isEdited,
  isValidPrice,
  isZeroGridPrice,
  otherMode,
  prefillValues,
  switchCurrency,
} from './overridePrice.utils';
import { OverridePriceCurrencyMode, OverridePriceDialogProps, OverridePriceValues } from './OverridePriceDialog.props';
import OverridePriceTable from './OverridePriceTable';

const OVERRIDE_PRICE_TITLE = 'Override Today\'s Price';

const buttonSx = {
  borderRadius: '4px',
  fontSize: 12,
  paddingX: 1.5,
  paddingY: 0.5,
  textTransform: 'none',
};

const OverridePriceDialog: FC<OverridePriceDialogProps> = ({ row, open, onClose }) => {
  const recordId = row?.record.id;
  const { data: form, error, isFetching } = useDailyNavOverrideForm(recordId, open);
  const { mutateAsync: overridePrice, isPending } = useOverrideDailyNavPrice();

  const [mode, setMode] = useState<OverridePriceCurrencyMode>('cap_table');
  const [values, setValues] = useState<OverridePriceValues>({});
  const [note, setNote] = useState('');
  // Once per open: whatever else arrives while it is open never touches the inputs.
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!open) {
      setInitialized(false);
    }
  }, [open]);

  // Prefill from the fetch this open triggered, not a cached form from a previous one.
  useEffect(() => {
    if (!open || !form || isFetching || initialized) {
      return;
    }
    // Entered in the company's cap table currency, the currency the SSV is stored in.
    setMode('cap_table');
    setValues(prefillValues(form, 'cap_table'));
    setNote('');
    setInitialized(true);
  }, [form, open, isFetching, initialized]);

  // Exactly the grid's rows; hidden ones post back at the form's price, keeping the SSV whole.
  const shown = useMemo(() => form?.securities.filter(security => security.is_shown) ?? [], [form]);
  const hiddenCount = (form?.securities.length ?? 0) - shown.length;

  const allValid = Boolean(form) && (form?.securities ?? []).every(security => isValidPrice(values[security.security_id] ?? ''));
  const zeroOnGrid = (form?.securities ?? []).some(security =>
    isZeroGridPrice(security, values[security.security_id] ?? '')
  );
  const anyEdited = Boolean(form) && (form?.securities ?? []).some(security => isEdited(security, values, form!, mode));
  const noteMissing = !note.trim();
  const canSubmit = Boolean(form) && allValid && !zeroOnGrid && anyEdited && !noteMissing && !isPending;

  const handleModeChange = (_: unknown, next: OverridePriceCurrencyMode | null) => {
    if (!form || !next || next === mode) {
      return;
    }
    setValues(switchCurrency(values, form, mode));
    setMode(next);
  };

  const handleSubmit = async () => {
    if (!form || recordId == null || !canSubmit) {
      return;
    }
    try {
      await overridePrice({ recordId, data: buildOverrideRequest(form, values, mode, note) });
      onClose();
    } catch {
      // Keep the dialog open; the mutation's onError already surfaced the reason.
    }
  };

  let submitHint: string | null = null;
  if (form && !allValid) {
    submitHint = 'Every security needs a price.';
  } else if (form && zeroOnGrid) {
    submitHint = 'The common and held securities need a price above zero.';
  } else if (form && !anyEdited) {
    submitHint = 'No prices changed.';
  } else if (form && noteMissing) {
    submitHint = 'A note is required.';
  }

  const renderBody = () => {
    if (error) {
      return (
        <Alert severity="error" data-testid="override-price-load-error">
          {error.message}
        </Alert>
      );
    }
    // Spinner only until this open's form is in; later fetches never unmount the inputs.
    if (!form || !initialized) {
      return (
        <Box display="flex" justifyContent="center" paddingY={4}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    const closingDate = shortDate(form.closing_measurement_date);
    return (
      <Box>
        <Typography sx={{ color: colors.slate500, fontSize: 12, marginBottom: 1 }}>
          {`${row?.companyName ?? ''} · Report date ${shortDate(form.report_date)}`}
        </Typography>
        <Alert severity="info" sx={{ fontSize: 11, marginBottom: 1.5 }}>
          {`Creates a FINAL "Daily NAV Override" valuation on ${shortDate(form.report_date)}`}
          {form.will_roll_over ? `, rolling the ${closingDate} valuation forward first.` : '.'}
          {` Current prices are from the ${closingDate} ${form.closing_is_override ? 'override' : 'valuation'}.`}
        </Alert>

        {hasCurrencyDifference(form) && (
          <Alert severity="warning" data-testid="override-currency-warning" sx={{ fontSize: 11, marginBottom: 1.5 }}>
            <strong>
              {`Prices are entered in ${currencyCode(form, mode)}`}
              {mode === 'cap_table' ? ` (${row?.companyName ?? 'the company'}'s cap table currency)` : ''}
              {`, not ${currencyCode(form, otherMode(mode))}.`}
            </strong>
            {canEnterInReporting(form)
              ? ` Daily NAV reports in ${currencyCode(form, 'reporting')}: 1 ${currencyCode(form, 'cap_table')} = ${Number(
                form.currency.fx_rate
              ).toFixed(4)} ${currencyCode(form, 'reporting')} on ${shortDate(form.currency.rate_date)}. The ${currencyCode(
                form,
                otherMode(mode)
              )} column shows each price converted.`
              : ` There is no ${currencyCode(form, 'cap_table')} to ${currencyCode(form, 'reporting')} rate for ${shortDate(
                form.currency.rate_date
              )}, so converted prices can't be shown.`}
          </Alert>
        )}

        {canEnterInReporting(form) && (
          <Box display="flex" alignItems="center" gap={1} marginBottom={1.5}>
            <Typography sx={{ color: colors.slate700, fontSize: 11, fontWeight: 600 }}>Enter prices in</Typography>
            <ToggleButtonGroup
              id="override-currency-toggle"
              exclusive
              size="small"
              value={mode}
              disabled={isPending}
              onChange={handleModeChange}>
              <ToggleButton value="cap_table" sx={{ fontSize: 11, paddingY: 0.25, textTransform: 'none' }}>
                {`${currencyCode(form, 'cap_table')} (cap table)`}
              </ToggleButton>
              <ToggleButton value="reporting" sx={{ fontSize: 11, paddingY: 0.25, textTransform: 'none' }}>
                {`${currencyCode(form, 'reporting')} (reporting)`}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <OverridePriceTable
          form={form}
          securities={shown}
          values={values}
          mode={mode}
          disabled={isPending}
          onChange={(id, value) => setValues({ ...values, [id]: value })}
        />
        {hiddenCount > 0 && (
          <Typography sx={{ color: colors.slate500, fontSize: 11, marginTop: 0.75 }}>
            {`${hiddenCount} other ${hiddenCount === 1 ? 'security keeps its' : 'securities keep their'} current price.`}
          </Typography>
        )}

        <Box marginTop={1.5}>
          <TextField
            id="override-note"
            label="Note (required)"
            value={note}
            onChange={event => setNote(event.target.value)}
            disabled={isPending}
            multiline
            minRows={2}
            fullWidth
            size="small"
            slotProps={{ input: { sx: { fontSize: 12 } } }}
          />
        </Box>
        {submitHint && (
          <Typography sx={{ color: colors.slate500, fontSize: 11, marginTop: 1 }} data-testid="override-submit-hint">
            {submitHint}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <FormDialog
      title={OVERRIDE_PRICE_TITLE}
      open={open}
      editMode
      isValid={canSubmit}
      isLoading={isPending}
      // No loadingText: Dialog hides its content whenever loadingText is set.
      showSaveButton={false}
      onClose={onClose}
      bigDialog
      customCancelButton={
        <Button
          id="override-cancel-button"
          onClick={onClose}
          disabled={isPending}
          sx={{ ...buttonSx, border: `1px solid ${colors.border}`, color: colors.slate700 }}>
          Cancel
        </Button>
      }
      customButton={
        <Button
          id="override-submit-button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            ...buttonSx,
            backgroundColor: colors.navy,
            color: '#fff',
            fontWeight: 600,
            '&:hover': { backgroundColor: colors.navyHover },
            '&.Mui-disabled': { backgroundColor: colors.border, color: colors.slate500 },
          }}>
          Override price
        </Button>
      }>
      <Box data-testid="override-price-dialog">{renderBody()}</Box>
    </FormDialog>
  );
};

export default OverridePriceDialog;
